import { useCallback } from "react";
import cx from "classnames";
import ComparativeAnalysisFormGroup from "./components/ComparativeAnalysisFormGroup/ComparativeAnalysisFormGroup";
import ComparativeAnalysisFormSubmit from "./components/ComparativeAnalysisFormSubmit/ComparativeAnalysisFormSubmit";
import ComparativeAnalysisGroupsSwapBtn from "./components/ComparativeAnalysisGroupsSwapBtn/ComparativeAnalysisGroupsSwapBtn";
import { useFormValidation } from "./hooks/useFormValidation";
import { useIsValidFormFields } from "./hooks/useIsValidFormFields";
import { useLocalFormGroupsState } from "./hooks/useLocalFormGroupsState";
import "./ComparativeAnalysisForm.css";

interface ComparativeAnalysisFormProps {
  className?: string;
  formOnly?: boolean;
  onSubmit?: () => void;
}

const ComparativeAnalysisForm = ({
  className,
  formOnly,
  ...props
}: ComparativeAnalysisFormProps) => {
  const { group1Props, group2Props, syncLocalStateToURL } =
    useLocalFormGroupsState();
  const { baseFieldsValidation, compareFieldsValidation } = useFormValidation(); // returns two objects which are collections of React states
  const {
    hasValidFormFields,
    identicalGroupFields,
    allFieldsEmpty,
    requiredFieldsEmpty,
  } = useIsValidFormFields(
    baseFieldsValidation,
    compareFieldsValidation,
    group1Props,
    group2Props
  );

  const onSubmit = useCallback(() => {
    props.onSubmit?.();
    return syncLocalStateToURL(); // important to return the form values, so they can be added to the history (inside ComparativeAnalysisFormSubmit)
  }, [props, syncLocalStateToURL]);

  return (
    <div
      className={cx("compare-versions-form w-full", className)}
      data-test-id="compare-runs--form"
    >
      <div className="flex w-full min-w-64 gap-3 items-center">
        <ComparativeAnalysisFormGroup
          {...group1Props}
          validators={baseFieldsValidation}
          className="compare-versions-form__group compare-versions-form__group--base flex flex-col gap-6 min-w-0"
        />

        <ComparativeAnalysisGroupsSwapBtn />

        <ComparativeAnalysisFormGroup
          {...group2Props}
          validators={compareFieldsValidation}
          className="compare-versions-form__group compare-versions-form__group--compare flex flex-col gap-6 min-w-0"
        />
      </div>

      <ComparativeAnalysisFormSubmit
        onSubmit={onSubmit}
        hasValidFormFields={hasValidFormFields}
        hasIdenticalGroupFields={identicalGroupFields}
        allFieldsEmpty={allFieldsEmpty}
        requiredFieldsEmpty={requiredFieldsEmpty}
      />
    </div>
  );
};

export default ComparativeAnalysisForm;
